import React, { useEffect, useState, useRef } from 'react';
import { Pane } from 'react-leaflet';
import { Marker, MarkerLayer } from 'react-leaflet-marker';
import { useRecoilValue } from 'recoil';
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import DeviceLocationIcon from './DeviceLocation.svg';
import type { Map as LeafletMap } from 'leaflet';
import { useRenderTracker } from '@app/shared/debug';

interface DeviceLocationV2Props {
	mapRef?: React.MutableRefObject<LeafletMap | null>;
	isTrackingRef?: React.MutableRefObject<boolean>;
}

export const DeviceLocationV2 = React.memo(function DeviceLocationV2({ mapRef, isTrackingRef }: DeviceLocationV2Props) {
	useRenderTracker('DeviceLocationV2');
	// Get geolocation from global atom (managed by GeolocationProvider)
	const geolocation = useRecoilValue(atom_geolocationState);
	const [continuousRotation, setContinuousRotation] = useState<number>(0);

	// Center map on user location when tracking is enabled and position updates
	useEffect(() => {
		if (!isTrackingRef?.current) return;
		if (mapRef?.current && geolocation.latitude && geolocation.longitude) {
			// Don't interfere with ongoing flyTo animations
			if ((mapRef.current as any)._flyToFrame) return;
			// Use animate: false to avoid leaving mapPane with a non-zero CSS transform
			// that causes a "jump" when the user starts dragging
			mapRef.current.setView([geolocation.latitude, geolocation.longitude], mapRef.current.getZoom(), { animate: false });
		}
	}, [geolocation.latitude, geolocation.longitude, mapRef, isTrackingRef]);

	// Listen to device compass orientation with filtering
	useEffect(() => {
		let compassReadings: number[] = [];
		const MAX_READINGS = 3; // Average over 3 readings to reduce jitter (was 5)
		let lastUpdateTime: number = 0;
		const UPDATE_INTERVAL = 100; // Update every 100ms (10Hz) — was 300ms (3.3Hz)
		const HEADING_DELTA_THRESHOLD = 3; // Only update state if heading changed > 3° (was 10°)
		let lastAppliedHeading: number | null = null;
		let rafId: number | null = null;

		const handleOrientation = (event: DeviceOrientationEvent) => {
			// Skip compass processing entirely when not tracking user location
			// if (isTrackingRef && !isTrackingRef.current) return;

			// Early return if still throttled - prevents expensive calculations
			const now = Date.now();
			if (now - lastUpdateTime < UPDATE_INTERVAL) {
				return;
			}

			// Use requestAnimationFrame to batch updates and not block the main thread
			if (rafId !== null) {
				cancelAnimationFrame(rafId);
			}

			rafId = requestAnimationFrame(() => {
				lastUpdateTime = now;

				let heading: number;

				// iOS Safari: webkitCompassHeading is clockwise from north
				if ('webkitCompassHeading' in event && typeof (event as any).webkitCompassHeading === 'number') {
					heading = (event as any).webkitCompassHeading as number;
				}
				// Android: raw alpha with absolute reference.
				// Render formula (rotate(-heading + 180)) expects raw alpha convention.
				// Only trust alpha when absolute; non-absolute has no north reference.
				else if (event.absolute && typeof event.alpha === 'number') {
					heading = event.alpha;
				} else {
					rafId = null;
					return;
				}

				// Compensate for screen rotation (landscape flips the reference frame)
				const screenAngle = (typeof screen !== 'undefined' && screen.orientation?.angle) || 0;
				heading = (heading + screenAngle) % 360;

				// Normalize to 0-360
				heading = ((heading % 360) + 360) % 360;

				// Add to readings buffer for averaging
				compassReadings.push(heading);
				if (compassReadings.length > MAX_READINGS) {
					compassReadings.shift();
				}

				// Calculate circular mean (handles wrap-around correctly)
				const avgHeading = (() => {
					let sinSum = 0;
					let cosSum = 0;
					compassReadings.forEach(h => {
						const rad = (h * Math.PI) / 180;
						sinSum += Math.sin(rad);
						cosSum += Math.cos(rad);
					});
					let mean = Math.atan2(sinSum / compassReadings.length, cosSum / compassReadings.length);
					mean = (mean * 180) / Math.PI;
					return ((mean + 360) % 360);
				})();

				// Only update state if heading changed significantly
				if (lastAppliedHeading !== null) {
					let headingDelta = Math.abs(avgHeading - lastAppliedHeading);
					if (headingDelta > 180) headingDelta = 360 - headingDelta;
					if (headingDelta < HEADING_DELTA_THRESHOLD) {
						rafId = null;
						return; // Skip — heading hasn't changed enough
					}
				}
				lastAppliedHeading = avgHeading;

				// Update continuous rotation to prevent wrap-around jumps
				setContinuousRotation(prev => {
					let delta = avgHeading - (prev % 360);
					if (delta > 180) delta -= 360;
					if (delta < -180) delta += 360;
					return prev + delta;
				});

				rafId = null;
			});
		};

		// Subscribe to one event only: prefer absolute (Android); fall back to
		// plain deviceorientation for iOS, which carries webkitCompassHeading.
		const addListeners = () => {
			if ('ondeviceorientationabsolute' in window) {
				window.addEventListener('deviceorientationabsolute', handleOrientation as any, true);
			} else {
				window.addEventListener('deviceorientation', handleOrientation, true);
			}
		};

		// Request permission for iOS 13+
		const requestPermission = async () => {
			if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
				try {
					const permission = await (DeviceOrientationEvent as any).requestPermission();
					if (permission === 'granted') {
						addListeners();
					}
				} catch (error) {
					console.warn('Device orientation permission denied:', error);
				}
			} else {
				addListeners();
			}
		};

		requestPermission();

		return () => {
			// Cancel any pending animation frame
			if (rafId !== null) {
				cancelAnimationFrame(rafId);
			}
			window.removeEventListener('deviceorientationabsolute', handleOrientation as any, true);
			window.removeEventListener('deviceorientation', handleOrientation, true);
		};
	}, []);

	// Don't render if we don't have a valid position
	if (!geolocation.latitude || !geolocation.longitude) {
		return null;
	}

	return (
		<Pane name="device-location" style={{ zIndex: 999999 }}>
			<MarkerLayer>
				<Marker
					key="device-location-marker"
					position={{
						lat: geolocation.latitude,
						lng: geolocation.longitude,
					}}
					placement="center"
					size={[0, 0]}
				>
					<img
						src={DeviceLocationIcon}
						alt=""
						aria-hidden="true"
						style={{
							position: 'absolute',
							left: '50%',
							top: '50%',
							transform: `translate(-50%, -50%) rotate(${-continuousRotation + 180}deg)`,
							width: '44px',
							height: '44px',
							transition: 'transform 0.15s ease-out',
						}}
					/>
				</Marker>
			</MarkerLayer>
		</Pane>
	);
});
